import { AddSmall } from '@wix/wix-ui-icons-common';
import { TextSelection } from 'prosemirror-state';
import React, { useContext } from 'react';

import { EditorContext, ModalServiceContext, RicosContext } from './../../../ricos-context';
import { UiComponentsProvider } from './../../../ui-components';

import { FLOATING_ADD_BUTTON_QUICK_ACTIONS } from '../data-hooks';
import { toggleQuickActionsModal } from '../toggle-quick-actions-modal/toggle-quick-actions-modal';
import styles from './floating-toolbar-button-scss';

type Position = { top: string };

type FloatingToolbarButtonProps = {
  className: string;
  position: Position;
};

const notHavingWordBeforeSlash = (text?: string) => {
  try {
    // Safari 15 does not support negative lookbehind and throws an error.
    // Needs to be as a string to avoid the error when safari is evaluating // type regexp
    return (text?.match(new RegExp('(?<!\\w)/\\w+ *\\w*$', 'gm'))?.length || 0) > 0;
  } catch (e) {
    console.warn('Error in notHavingWordBeforeSlash', e);
    return false;
  }
};

export const FloatingToolbarButton: React.FC<FloatingToolbarButtonProps> = ({
  className,
  position,
}) => {
  const { adapter } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext);
  const { t } = useContext(RicosContext);

  const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

  const handleClick = () => {
    const { view } = adapter.tiptapEditor;

    const { nodeBefore } = view.state.selection.$anchor;

    // when some text is selected, selection needs to be moved to the end of  the paragraph so that the content does not get replaced
    if (!view.state.selection.empty) {
      const selectionUpdated = TextSelection.create(view.state.doc, view.state.selection.to);
      view.dispatch(view.state.tr.setSelection(selectionUpdated));
    }

    // if the quick actions modal is already open trough the editor, does not open the modal again
    if (
      view.state.selection.empty &&
      (nodeBefore?.text === '/' ||
        nodeBefore?.text?.endsWith(' /') ||
        notHavingWordBeforeSlash(nodeBefore?.text))
    ) {
      view.focus();
      return;
    }
    toggleQuickActionsModal({ modalService, referenceElement: ref });
  };

  return (
    <div className={className} style={position} ref={setRef}>
      {ref && (
        <UiComponentsProvider containerEl={ref}>
          <button
            aria-label={t('QuickActionsPlugin_AddButton_AriaLabel')}
            type="button"
            className={styles.button}
            onClick={handleClick}
            data-hook={FLOATING_ADD_BUTTON_QUICK_ACTIONS}
            aria-haspopup="true"
          >
            <AddSmall fill="currentColor" />
          </button>
        </UiComponentsProvider>
      )}
    </div>
  );
};
